import * as Schema from "effect/Schema";

import { NonNegativeInt, PositiveInt, TrimmedNonEmptyString } from "./baseSchemas.ts";

/**
 * Spotify sidecar protocol v1 — one JSON object per line, commands on stdin,
 * events on stdout. Field names are camelCase. Source of truth shared with
 * the engine crate (mock today at APPS/cleo-code/native/spotify-engine, later
 * native/spotify-engine); keep in sync with its protocol.rs. Playback is
 * process-global: one sidecar, one session, one queue — never per-thread.
 */
export const SPOTIFY_PROTOCOL_VERSION = 1 as const;

export const SpotifyGrant = Schema.Literals(["web", "playback"]);
export type SpotifyGrant = typeof SpotifyGrant.Type;

export const SpotifyAuthStatus = Schema.Literals([
  "signedOut",
  "waitingForBrowser",
  "connecting",
  "connected",
  "failed",
  "premiumRequired",
]);
export type SpotifyAuthStatus = typeof SpotifyAuthStatus.Type;

export const SpotifyPlayback = Schema.Literals(["stopped", "loading", "playing", "paused"]);
export type SpotifyPlayback = typeof SpotifyPlayback.Type;

export const SpotifyRepeat = Schema.Literals(["off", "context", "track"]);
export type SpotifyRepeat = typeof SpotifyRepeat.Type;

export const SpotifyErrorCode = Schema.Literals([
  "invalid-command",
  "protocol-mismatch",
  "not-configured",
  "not-signed-in",
  "sign-in-expired",
  "premium-required",
  "rate-limited",
  // No Spotify device is active and none could be made so (6 Oct 2026):
  // surfaces offer "Play here" — the Spotify app on this Mac — beside the
  // engine's sentence, never Spotify's raw "No active device found".
  "no-active-device",
  // There is no Spotify app here to open: no "Play here" offer.
  "spotify-not-installed",
  // The Spotify app was opened but has not announced its device in time.
  "spotify-starting",
  // Spotify took a Play but nothing started on its device within a few
  // seconds (W-61b13127): the engine names the device; surfaces offer
  // "Play here" beside it, never a silent "Nothing playing".
  "not-started",
  "spotify",
]);
export type SpotifyErrorCode = typeof SpotifyErrorCode.Type;

export const SpotifyTrack = Schema.Struct({
  uri: TrimmedNonEmptyString,
  title: Schema.String,
  artists: Schema.Array(Schema.String),
  album: Schema.String,
  artUrl: Schema.NullOr(Schema.String),
  durationMs: NonNegativeInt,
  isEpisode: Schema.Boolean,
});
export type SpotifyTrack = typeof SpotifyTrack.Type;

export const SpotifyDevice = Schema.Struct({
  id: TrimmedNonEmptyString,
  name: Schema.String,
  kind: Schema.String,
  isActive: Schema.Boolean,
  isLocal: Schema.Boolean,
  volumePercent: NonNegativeInt,
});
export type SpotifyDevice = typeof SpotifyDevice.Type;

export const SpotifyCapabilities = Schema.Struct({
  webApi: Schema.Boolean,
  localPlayback: Schema.Boolean,
  mdnsConnect: Schema.Boolean,
});
export type SpotifyCapabilities = typeof SpotifyCapabilities.Type;

export const SpotifyCollectionKind = Schema.Literals(["playlist", "album", "likedSongs"]);
export type SpotifyCollectionKind = typeof SpotifyCollectionKind.Type;

export const SpotifyCollectionRef = Schema.Struct({
  kind: SpotifyCollectionKind,
  id: TrimmedNonEmptyString,
});
export type SpotifyCollectionRef = typeof SpotifyCollectionRef.Type;

export const SpotifyPlaylistCard = Schema.Struct({
  uri: TrimmedNonEmptyString,
  id: TrimmedNonEmptyString,
  name: Schema.String,
  ownerName: Schema.String,
  description: Schema.NullOr(Schema.String),
  artUrl: Schema.NullOr(Schema.String),
  itemCount: NonNegativeInt,
});
export type SpotifyPlaylistCard = typeof SpotifyPlaylistCard.Type;

export const SpotifyAlbumCard = Schema.Struct({
  uri: TrimmedNonEmptyString,
  id: TrimmedNonEmptyString,
  name: Schema.String,
  artists: Schema.Array(Schema.String),
  artUrl: Schema.NullOr(Schema.String),
  year: Schema.NullOr(Schema.String),
});
export type SpotifyAlbumCard = typeof SpotifyAlbumCard.Type;

export const SpotifyArtistCard = Schema.Struct({
  uri: TrimmedNonEmptyString,
  id: TrimmedNonEmptyString,
  name: Schema.String,
  artUrl: Schema.NullOr(Schema.String),
});
export type SpotifyArtistCard = typeof SpotifyArtistCard.Type;

const version = Schema.Literal(SPOTIFY_PROTOCOL_VERSION);

export const SpotifyConfigureCommand = Schema.Struct({
  version,
  type: Schema.Literal("configure"),
  deviceName: TrimmedNonEmptyString,
  webClientId: Schema.optionalKey(TrimmedNonEmptyString),
});
export type SpotifyConfigureCommand = typeof SpotifyConfigureCommand.Type;

export const SpotifySignInWebCommand = Schema.Struct({
  version,
  type: Schema.Literal("signInWeb"),
});
export const SpotifyCancelSignInCommand = Schema.Struct({
  version,
  type: Schema.Literal("cancelSignIn"),
});
export const SpotifyToggleCommand = Schema.Struct({
  version,
  type: Schema.Literal("toggle"),
});
export const SpotifyPlayCommand = Schema.Struct({ version, type: Schema.Literal("play") });
export const SpotifyPauseCommand = Schema.Struct({ version, type: Schema.Literal("pause") });
export const SpotifyNextCommand = Schema.Struct({ version, type: Schema.Literal("next") });
export const SpotifyPreviousCommand = Schema.Struct({
  version,
  type: Schema.Literal("previous"),
});
export const SpotifySeekCommand = Schema.Struct({
  version,
  type: Schema.Literal("seek"),
  positionMs: NonNegativeInt,
});
export const SpotifyVolumeCommand = Schema.Struct({
  version,
  type: Schema.Literal("volume"),
  percent: NonNegativeInt,
});
export const SpotifyVolumePreviewCommand = Schema.Struct({
  version,
  type: Schema.Literal("volumePreview"),
  percent: NonNegativeInt,
});
export const SpotifyShuffleCommand = Schema.Struct({
  version,
  type: Schema.Literal("shuffle"),
  enabled: Schema.Boolean,
});
export const SpotifyRepeatCommand = Schema.Struct({
  version,
  type: Schema.Literal("repeat"),
  mode: SpotifyRepeat,
});
export const SpotifyTransferCommand = Schema.Struct({
  version,
  type: Schema.Literal("transfer"),
  deviceId: TrimmedNonEmptyString,
  play: Schema.Boolean,
});
/**
 * "Play here" (the principal, 6 Oct 2026): the Spotify app on this Mac —
 * opened when it is closed — takes playback and plays. Not a player in Cleo.
 */
export const SpotifyPlayHereCommand = Schema.Struct({
  version,
  type: Schema.Literal("playHere"),
});
export const SpotifyLoadCommand = Schema.Struct({
  version,
  type: Schema.Literal("load"),
  contextUri: Schema.optionalKey(TrimmedNonEmptyString),
  uris: Schema.optionalKey(Schema.Array(TrimmedNonEmptyString)),
  offsetUri: Schema.optionalKey(TrimmedNonEmptyString),
  play: Schema.Boolean,
});
export type SpotifyLoadCommand = typeof SpotifyLoadCommand.Type;
export const SpotifyAddToQueueCommand = Schema.Struct({
  version,
  type: Schema.Literal("addToQueue"),
  uri: TrimmedNonEmptyString,
});
export const SpotifyListDevicesCommand = Schema.Struct({
  version,
  type: Schema.Literal("listDevices"),
  requestId: TrimmedNonEmptyString,
});
export const SpotifySearchCommand = Schema.Struct({
  version,
  type: Schema.Literal("search"),
  requestId: TrimmedNonEmptyString,
  query: Schema.String,
});
export const SpotifyGetLibraryCommand = Schema.Struct({
  version,
  type: Schema.Literal("getLibrary"),
  requestId: TrimmedNonEmptyString,
});
export const SpotifyGetHomeCommand = Schema.Struct({
  version,
  type: Schema.Literal("getHome"),
  requestId: TrimmedNonEmptyString,
});
export const SpotifyCreatePlaylistCommand = Schema.Struct({
  version,
  type: Schema.Literal("createPlaylist"),
  requestId: TrimmedNonEmptyString,
  name: TrimmedNonEmptyString,
  description: Schema.String,
  uris: Schema.Array(TrimmedNonEmptyString),
});
export const SpotifyGetCollectionCommand = Schema.Struct({
  version,
  type: Schema.Literal("getCollection"),
  requestId: TrimmedNonEmptyString,
  collection: SpotifyCollectionRef,
});
export const SpotifyGetSnapshotCommand = Schema.Struct({
  version,
  type: Schema.Literal("getSnapshot"),
  requestId: TrimmedNonEmptyString,
});
export const SpotifyShutdownCommand = Schema.Struct({
  version,
  type: Schema.Literal("shutdown"),
});

export const SpotifyCommand = Schema.Union([
  SpotifyConfigureCommand,
  SpotifySignInWebCommand,
  SpotifyCancelSignInCommand,
  SpotifyToggleCommand,
  SpotifyPlayCommand,
  SpotifyPauseCommand,
  SpotifyNextCommand,
  SpotifyPreviousCommand,
  SpotifySeekCommand,
  SpotifyVolumeCommand,
  SpotifyVolumePreviewCommand,
  SpotifyShuffleCommand,
  SpotifyRepeatCommand,
  SpotifyTransferCommand,
  SpotifyPlayHereCommand,
  SpotifyLoadCommand,
  SpotifyAddToQueueCommand,
  SpotifyListDevicesCommand,
  SpotifySearchCommand,
  SpotifyGetLibraryCommand,
  SpotifyGetHomeCommand,
  SpotifyGetCollectionCommand,
  SpotifyCreatePlaylistCommand,
  SpotifyGetSnapshotCommand,
  SpotifyShutdownCommand,
]);
export type SpotifyCommand = typeof SpotifyCommand.Type;

/**
 * The command surface a user-facing client (footer strip, music surface,
 * agent tools) may send. The server owns configure and shutdown; the
 * request/response commands ride dedicated round-trip doors instead.
 */
export const SpotifyUserCommand = Schema.Union([
  SpotifySignInWebCommand,
  SpotifyCancelSignInCommand,
  SpotifyToggleCommand,
  SpotifyPlayCommand,
  SpotifyPauseCommand,
  SpotifyNextCommand,
  SpotifyPreviousCommand,
  SpotifySeekCommand,
  SpotifyVolumeCommand,
  SpotifyVolumePreviewCommand,
  SpotifyShuffleCommand,
  SpotifyRepeatCommand,
  SpotifyTransferCommand,
  SpotifyPlayHereCommand,
  SpotifyLoadCommand,
  SpotifyAddToQueueCommand,
]);
export type SpotifyUserCommand = typeof SpotifyUserCommand.Type;

export const SpotifyHelloEvent = Schema.Struct({
  version,
  type: Schema.Literal("hello"),
  sidecarVersion: TrimmedNonEmptyString,
  sidecarPid: PositiveInt,
  platform: Schema.String,
  arch: Schema.String,
  capabilities: SpotifyCapabilities,
});
export type SpotifyHelloEvent = typeof SpotifyHelloEvent.Type;

export const SpotifyAuthEvent = Schema.Struct({
  version,
  type: Schema.Literal("auth"),
  grant: SpotifyGrant,
  status: SpotifyAuthStatus,
  url: Schema.NullOr(Schema.String),
  username: Schema.NullOr(Schema.String),
  message: Schema.NullOr(Schema.String),
});
export type SpotifyAuthEvent = typeof SpotifyAuthEvent.Type;

export const SpotifySnapshotEvent = Schema.Struct({
  version,
  type: Schema.Literal("snapshot"),
  requestId: Schema.NullOr(Schema.String),
  sequence: NonNegativeInt,
  playback: SpotifyPlayback,
  track: Schema.NullOr(SpotifyTrack),
  positionMs: NonNegativeInt,
  positionAtUnixMs: Schema.NullOr(NonNegativeInt),
  volumePercent: NonNegativeInt,
  shuffle: Schema.Boolean,
  repeat: SpotifyRepeat,
  activeDeviceId: Schema.NullOr(Schema.String),
  activeDeviceName: Schema.NullOr(Schema.String),
  isLocalDevice: Schema.Boolean,
  webAuth: SpotifyAuthStatus,
  playbackAuth: SpotifyAuthStatus,
  error: Schema.NullOr(Schema.String),
});
export type SpotifySnapshotEvent = typeof SpotifySnapshotEvent.Type;

export const SpotifyDevicesEvent = Schema.Struct({
  version,
  type: Schema.Literal("devices"),
  requestId: TrimmedNonEmptyString,
  devices: Schema.Array(SpotifyDevice),
});
export type SpotifyDevicesEvent = typeof SpotifyDevicesEvent.Type;

export const SpotifySearchResultEvent = Schema.Struct({
  version,
  type: Schema.Literal("searchResult"),
  requestId: TrimmedNonEmptyString,
  query: Schema.String,
  tracks: Schema.Array(SpotifyTrack),
});
export type SpotifySearchResultEvent = typeof SpotifySearchResultEvent.Type;

export const SpotifyLibraryResultEvent = Schema.Struct({
  version,
  type: Schema.Literal("libraryResult"),
  requestId: TrimmedNonEmptyString,
  playlists: Schema.Array(SpotifyPlaylistCard),
  albums: Schema.Array(SpotifyAlbumCard),
  artists: Schema.Array(SpotifyArtistCard),
  likedCount: NonNegativeInt,
});
export type SpotifyLibraryResultEvent = typeof SpotifyLibraryResultEvent.Type;

export const SpotifyHomeResultEvent = Schema.Struct({
  version,
  type: Schema.Literal("homeResult"),
  requestId: TrimmedNonEmptyString,
  pinned: Schema.Array(SpotifyPlaylistCard),
  madeForYou: Schema.Array(SpotifyPlaylistCard),
  recentlyPlayed: Schema.Array(SpotifyTrack),
  topArtists: Schema.Array(SpotifyArtistCard),
  topTracks: Schema.Array(SpotifyTrack),
  recommendations: Schema.Array(SpotifyTrack),
});
export type SpotifyHomeResultEvent = typeof SpotifyHomeResultEvent.Type;

export const SpotifyCollectionResultEvent = Schema.Struct({
  version,
  type: Schema.Literal("collectionResult"),
  requestId: TrimmedNonEmptyString,
  kind: SpotifyCollectionKind,
  id: Schema.String,
  // Liked Songs has no context uri; the surface plays its rows as a uris load.
  uri: Schema.String,
  name: Schema.String,
  owner: Schema.String,
  description: Schema.NullOr(Schema.String),
  artUrl: Schema.NullOr(Schema.String),
  items: Schema.Array(SpotifyTrack),
});
export type SpotifyCollectionResultEvent = typeof SpotifyCollectionResultEvent.Type;

export const SpotifyErrorEvent = Schema.Struct({
  version,
  type: Schema.Literal("error"),
  code: SpotifyErrorCode,
  message: Schema.String,
  recoverable: Schema.Boolean,
  requestId: Schema.NullOr(Schema.String),
});
export type SpotifyErrorEvent = typeof SpotifyErrorEvent.Type;

export const SpotifyEvent = Schema.Union([
  SpotifyHelloEvent,
  SpotifyAuthEvent,
  SpotifySnapshotEvent,
  SpotifyDevicesEvent,
  SpotifySearchResultEvent,
  SpotifyLibraryResultEvent,
  SpotifyHomeResultEvent,
  SpotifyCollectionResultEvent,
  SpotifyErrorEvent,
]);
export type SpotifyEvent = typeof SpotifyEvent.Type;

export const SpotifyEngineStatus = Schema.Literals([
  "starting",
  "healthy",
  "degraded",
  "unavailable",
  "stopped",
]);
export type SpotifyEngineStatus = typeof SpotifyEngineStatus.Type;

/**
 * The composed last-known state a client needs to render chrome: engine
 * standing, handshake, latest auth per grant, and the latest snapshot. The
 * server publishes a fresh view on every engine event; clients render the
 * view and never talk to Spotify directly.
 */
export const SpotifyEngineView = Schema.Struct({
  status: SpotifyEngineStatus,
  hello: Schema.NullOr(SpotifyHelloEvent),
  webAuth: Schema.NullOr(SpotifyAuthEvent),
  playbackAuth: Schema.NullOr(SpotifyAuthEvent),
  snapshot: Schema.NullOr(SpotifySnapshotEvent),
  lastError: Schema.NullOr(Schema.String),
  /** The engine's code for the last refusal; null for a crash or none. */
  lastErrorCode: Schema.NullOr(SpotifyErrorCode),
  /**
   * Counts refusals, so a surface shows a repeated identical one again: the
   * same sentence twice is two presses that both failed.
   */
  lastErrorSequence: NonNegativeInt,
});
export type SpotifyEngineView = typeof SpotifyEngineView.Type;

/**
 * Wire-level refusal for Spotify engine calls: the server maps its typed
 * engine errors onto this one serializable class so clients render reason
 * and code without importing server internals.
 */
export class SpotifyEngineCallError extends Schema.TaggedErrorClass<SpotifyEngineCallError>()(
  "SpotifyEngineCallError",
  {
    reason: Schema.String,
    code: Schema.NullOr(Schema.String),
  },
) {
  override get message(): string {
    return this.reason;
  }
}

/** Footer display position: interpolate only while playing. */
export function displayedSpotifyPositionMs(
  snapshot: SpotifySnapshotEvent,
  nowUnixMs: number,
): number {
  const duration = snapshot.track?.durationMs ?? Number.POSITIVE_INFINITY;
  if (snapshot.playback !== "playing" || snapshot.positionAtUnixMs === null) {
    return Math.min(snapshot.positionMs, duration);
  }
  const elapsed = Math.max(0, nowUnixMs - snapshot.positionAtUnixMs);
  return Math.min(snapshot.positionMs + elapsed, duration);
}
